import React, { useCallback, useState } from "react";
import {
    Frame, Navigation, TopBar, Page,
    SkeletonBodyText,
    Banner
} from "@shopify/polaris";
import Routes from "./pages/home/Routes";
import {
    ArrowLeftMinor,
    AutomationMajor,
    SettingsMajor,
    ResourcesMajor,
    ChecklistMajor,
    CircleInformationMajor,
    PaymentsMajor,
    DataVisualizationMajor,
    CollectionsMajor,
    CirclePlusOutlineMinor
} from '@shopify/polaris-icons';
import { useLocation, useHistory } from "react-router-dom";
import { Helmet } from 'react-helmet';
import WelcomeSurvey from './pages/home/WelcomeSurvey';
import FeedbackSurvey from './pages/home/FeedbackSurvey';
import UnsuspendStore from './pages/home/UnsuspendStore';
import ChoosePlanPage from './pages/ChoosePlanPage';
import { AppContext } from './common/AppContext';

export function AppFrame(props) {

    const history = useHistory();
    const appContext = React.useContext(AppContext);
    const backUrl = "http://" + props.shopOrigin + "/admin";
    const location = useLocation();
    const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
    const toggleMobileNavigationActive = useCallback(
        () =>
            setMobileNavigationActive(
                (mobileNavigationActive) => !mobileNavigationActive,
            ),
        [],
    );
    let organizeItems = [
        {
            label: 'Tag Rules',
            icon: ChecklistMajor,
            url: "/tag-rules",
        },
        {
            label: 'Tag Groups',
            icon: ChecklistMajor,
            url: "/tag-groups",
        }
    ]

    if (props.enabledFeatures.includes("validationRulesNew")) {
        organizeItems.push({
            label: 'Validation Rules',
            icon: ChecklistMajor,
            url: "/validation-rules",
        },);
    }

    if (props.showValidationNavigation) {
        organizeItems.push({
            label: 'Validations',
            icon: ChecklistMajor,
            url: "/validations",
        });
    }

    const userMenuMarkup = (
        <TopBar.UserMenu
            name={props.shopOrigin}
            detail={"Store"}
        />
    );

    const topBarMarkup = (
        <TopBar userMenu={userMenuMarkup}
            onNavigationToggle={toggleMobileNavigationActive}
            showNavigationToggle
        />
    );

    if (props.shopKey == null || props.shopOrigin == null) {
        return (<Frame topBar={topBarMarkup}>
            <Helmet>
                <title>Tag Genie - Dashboard</title>
            </Helmet>
            <Page title="Dashboard">
                <Banner
                    title="Session expired"
                    status="critical"
                >
                    <p>
                        You have been logged out automatically. Please, restart the application from Shopify admin panel.
                    </p>
                </Banner>
            </Page>
        </Frame>);
    }

    const logo = {
        width: 124,
        topBarSource:
            'https://taggeniemain.blob.core.windows.net/images/Original%20on%20Transparent%20-%20horizontal.png',
        url: 'http://taggenieapp.com',
        accessibilityLabel: 'Tag Genie',
    };

    if (props.isLoading) {
        return (
            <Frame topBar={topBarMarkup} logo={logo}>
                <Helmet>
                    <title>Tag Genie</title>
                </Helmet>
                <Page title="Loading...">
                    <SkeletonBodyText lines="20" />
                </Page>
            </Frame>
        );
    }

    if (!props.isSubscribed && !props.isPrePlanChange) {
        return <Frame topBar={topBarMarkup}>
            <ChoosePlanPage />
        </Frame>
    }

    if (!props.completedSurvey) {
        return <Frame topBar={topBarMarkup}>
            <WelcomeSurvey
                email={props.email}
                onCompleteSurvey={props.onCompleteSurvey} />
        </Frame>
    }

    if (appContext.shouldCompleteFeedbackSurvey) {
        return <Frame topBar={topBarMarkup}>
            <FeedbackSurvey
                onSurveyCompleted={() => appContext.onCompleteFeedbackSurvey()} />
        </Frame>
    }

    if (props.isSuspended) {
        return <Frame topBar={topBarMarkup}>
            <UnsuspendStore onUnsuspend={props.onUnsuspend} />
        </Frame>
    }

    let navigationMarkup = (
        <Navigation location={location.pathname}>
            <Navigation.Section
                items={[
                    {
                        label: 'Back to Shopify',
                        icon: ArrowLeftMinor,
                        url: backUrl,
                    },
                ]}
            />
            <Navigation.Section
                items={[
                    {
                        label: 'Dashboard',
                        icon: DataVisualizationMajor,
                        url: "/",
                        exactMatch: true
                    },
                ]}
            />
            <Navigation.Section
                title="Bulk Edit"
                items={[
                    {
                        label: 'Tag Explorer',
                        icon: CollectionsMajor,
                        url: "/explorer",
                        new: true,
                        exactMatch: true
                    },
                    {
                        label: 'Bulk Operations',
                        icon: AutomationMajor,
                        url: "/operations",
                        secondaryAction: {
                            url: '/operations/new',
                            accessibilityLabel: 'Create bulk operation',
                            icon: CirclePlusOutlineMinor,
                            tooltip: {
                                content: 'Create bulk operation',
                            },
                            onClick: (e) => { e.preventDefault(); history.push("/operations/new") }
                        },
                    }
                ]}
            />
            <Navigation.Section
                title="Organize & Automate"
                items={organizeItems}
            />
            <Navigation.Section
                title="Automate"
                items={[
                    {
                        label: 'Automations',
                        icon: ChecklistMajor,
                        url: "/automations",
                    }
                ]}
            />
            <Navigation.Section
                separator
                title="Settings & Help"
                items={[
                    {
                        label: 'Tutorials',
                        icon: ResourcesMajor,
                        url: "/tutorials",
                    },
                    {
                        label: 'Plan',
                        icon: PaymentsMajor,
                        url: "/plan",
                    },
                    {
                        label: 'Help & Support',
                        icon: CircleInformationMajor,
                        url: "/help",
                    },
                    {
                        label: 'Settings',
                        icon: SettingsMajor,
                        url: "/settings",
                    },
                ]}
            />
        </Navigation>

    );

    if (appContext.hideNavigation) {
        navigationMarkup = null;
    }

    return (
        <Frame
            logo={logo}
            showMobileNavigation={mobileNavigationActive}
            onNavigationDismiss={toggleMobileNavigationActive}
            navigation={navigationMarkup}
            topBar={topBarMarkup}>
            <Routes />
        </Frame>
    );

}

export default AppFrame;